import React, {
  createContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { useAuth } from '../hooks/useAuth';
import { getAuthToken } from '../utils/auth';

const PING_INTERVAL_MS = 25_000;
const PONG_TIMEOUT_MS = 10_000;
const RECONNECT_DELAYS = [1000, 2000, 5000, 10_000, 30_000];

export const RealtimeContext = createContext(null);

/**
 * Normalize the configured WebSocket base URL into a handshake endpoint.
 * Accepts both origin-only values (`wss://host`) and `/ws`-suffixed values
 * (`wss://host/ws`) so a minor env typo does not silently break realtime.
 */
export function buildWsHandshakeUrl(rawBaseUrl, token) {
  const fallback = 'ws://localhost:8080';
  const source = String(rawBaseUrl ?? '').trim() || fallback;

  try {
    const url = new URL(source);
    url.hash = '';
    const normalizedPath = url.pathname.replace(/\/+$/, '');
    url.pathname = !normalizedPath || normalizedPath === '/' ? '/ws' : normalizedPath;
    url.search = '';
    url.searchParams.set('token', token);
    return url.toString();
  } catch {
    const compact = source.replace(/\s+/g, '');
    const withProtocol = /^wss?:\/\//i.test(compact) ? compact : `ws://${compact}`;
    const withoutTrailingSlash = withProtocol.replace(/\/+$/, '');
    const base = withoutTrailingSlash.endsWith('/ws') ? withoutTrailingSlash : `${withoutTrailingSlash}/ws`;
    return `${base}?token=${encodeURIComponent(token)}`;
  }
}

/**
 * ONE WebSocket for the whole app (Rule 21).
 *
 * This lives in a provider rather than in the hook because the connection is a
 * process-level resource: the shell subscribes to `admin:control`, a page
 * subscribes to `member:M-0001`, and both must ride the same socket. A hook that
 * owned its own refs would open a fresh connection per calling component, and
 * the server would see N sockets per user where it expects one.
 *
 * Rooms are joined on subscribe and re-joined automatically after a reconnect,
 * so a dropped connection restores the caller's subscriptions without the caller
 * knowing a drop happened.
 */
export function RealtimeProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState('disconnected');

  const wsRef = useRef(null);
  const handlersRef = useRef(new Map());        // Map<room, Set<handler>>
  const globalRef = useRef(new Set());          // handlers that want every event
  const pingTimerRef = useRef(null);
  const pongTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const manualCloseRef = useRef(false);
  const joinedRoomsRef = useRef(new Set());

  const dispatch = useCallback((payload) => {
    // Every room event carries `room` (docs/websocket-protocol.md §3). Per-user
    // control events deliberately do not — they reach subscribeAll only.
    const room = payload?.room;
    if (room && handlersRef.current.has(room)) {
      for (const h of handlersRef.current.get(room)) h(payload);
    }
    for (const h of globalRef.current) h(payload);
  }, []);

  const sendRaw = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }, []);

  const startPing = useCallback(() => {
    clearInterval(pingTimerRef.current);
    pingTimerRef.current = setInterval(() => {
      if (!sendRaw({ type: 'PING' })) return;
      clearTimeout(pongTimerRef.current);
      pongTimerRef.current = setTimeout(() => {
        // No PONG — the socket is a zombie. Close so onclose reconnects us.
        try { wsRef.current?.close(); } catch { /* already gone */ }
      }, PONG_TIMEOUT_MS);
    }, PING_INTERVAL_MS);
  }, [sendRaw]);

  const connect = useCallback(() => {
    const token = getAuthToken();
    if (!token) {
      setStatus('disconnected');
      return;
    }

    const url = buildWsHandshakeUrl(process.env.REACT_APP_WS_URL, token);
    setStatus('connecting');
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('connected');
      reconnectAttemptRef.current = 0;
      for (const room of joinedRoomsRef.current) {
        ws.send(JSON.stringify({ type: 'JOIN_ROOM', room }));
      }
      startPing();
    };

    ws.onmessage = (event) => {
      let data = null;
      try { data = JSON.parse(event.data); } catch { return; }
      if (!data || typeof data !== 'object') return;

      if (data.type === 'PONG') {
        clearTimeout(pongTimerRef.current);
        return;
      }

      // TOKEN_REVOKED means the session is gone server-side (sign-out elsewhere,
      // password change, admin revoke). Treat it exactly like a 401.
      if (data.type === 'TOKEN_REVOKED'
        || (data.type === 'ERROR' && /unauthorized/i.test(data.message || ''))) {
        manualCloseRef.current = true;
        try { ws.close(); } catch { /* already gone */ }
        window.location.reload();
        return;
      }

      dispatch(data);
    };

    ws.onclose = () => {
      clearInterval(pingTimerRef.current);
      clearTimeout(pongTimerRef.current);
      wsRef.current = null;
      setStatus('disconnected');
      if (manualCloseRef.current) return;

      const attempt = Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1);
      reconnectAttemptRef.current += 1;
      reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAYS[attempt]);
    };

    ws.onerror = () => {
      // onclose always follows; the reconnect is handled there.
    };
  }, [dispatch, startPing]);

  useEffect(() => {
    if (!isAuthenticated) {
      manualCloseRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      try { wsRef.current?.close(); } catch { /* already gone */ }
      return undefined;
    }

    manualCloseRef.current = false;
    connect();

    return () => {
      manualCloseRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      clearInterval(pingTimerRef.current);
      clearTimeout(pongTimerRef.current);
      try { wsRef.current?.close(); } catch { /* already gone */ }
    };
  }, [isAuthenticated, connect]);

  /** Join a room and register a handler. Returns the unsubscribe function. */
  const subscribe = useCallback((room, handler) => {
    if (!room) return () => {};

    if (!handlersRef.current.has(room)) handlersRef.current.set(room, new Set());
    handlersRef.current.get(room).add(handler);

    if (!joinedRoomsRef.current.has(room)) {
      joinedRoomsRef.current.add(room);
      sendRaw({ type: 'JOIN_ROOM', room });
    }

    return () => {
      const set = handlersRef.current.get(room);
      if (!set) return;

      set.delete(handler);
      if (set.size === 0) {
        handlersRef.current.delete(room);
        joinedRoomsRef.current.delete(room);
        sendRaw({ type: 'LEAVE_ROOM', room });
      }
    };
  }, [sendRaw]);

  /** Every event, room or not — for the notification bell and toasts. */
  const subscribeAll = useCallback((handler) => {
    globalRef.current.add(handler);
    return () => globalRef.current.delete(handler);
  }, []);

  const value = useMemo(
    () => ({ status, send: sendRaw, subscribe, subscribeAll }),
    [status, sendRaw, subscribe, subscribeAll],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
